import PropTypes from 'prop-types'
import { SIZE } from 'baseui/button'
import { Modal, ModalBody, ModalButton, ModalFooter, ModalHeader } from 'baseui/modal'
import { Spinner } from 'baseui/spinner'
import SDButton from '@/shared/components/SDComponents/SDButton/SDButton'
import RetryModalBody from './RetryModalBody/RetryModalBody'
import useRetryLeadButton from './useRetryLeadButton'
import { RetryHelperText } from '../styled-components'
import { useSaleShell } from '../saleShellContext'
import ShellConfirmModal from '../../AdditionalSalesV2/ShellConfirmModal'
import IconHelp from '@/shared/icons/NewDesign/IconHelp'
import IconRestart from '@/shared/icons/NewDesign/IconRestart'
import IconWarning from '@/shared/icons/NewDesign/IconWarning'
import {
  ActionRow,
  DirectionIcon,
  RetryBox,
  RetryHelp,
  RetryLine,
  ShellButton,
} from '../../AdditionalSalesV2/styled-components'

const RetryLeadButton = ({
  actionsInProgress,
  updateLeadDispatch,
  saleDataState,
  callback,
  hasBeenRetried,
  creationDate,
  saleType,
  hidden,
}) => {
  const {
    t,
    retryText,
    buttonText,
    helperText,
    confirmationRequired,
    emailChecked,
    spamChecked,
    isEnabled,
    isConfirmEnabled,
    isRetrying,
    isModalOpen,
    isInlineConfirm,
    handleRetryClick,
    handleConfirm,
    handleCancel,
    handleEmailChange,
    handleSpamChange,
  } = useRetryLeadButton({
    actionsInProgress,
    updateLeadDispatch,
    saleDataState,
    callback,
    hasBeenRetried,
    creationDate,
    saleType,
  })

  const isShell = useSaleShell()

  if (hidden) {
    return null
  }

  if (isShell) {
    return (
      <RetryBox>
        {isRetrying && (
          <RetryLine>
            <Spinner $size='18px' $borderWidth='2px' />
            {t('additionalSales:common.retry_in_progress')}
          </RetryLine>
        )}
        {!isRetrying && isInlineConfirm && (
          <>
            <RetryLine>
              <DirectionIcon>
                <IconWarning size={20} color='currentColor' />
              </DirectionIcon>
              {t('additionalSales:pulse.retry.confirm')}
            </RetryLine>
            <ActionRow $center>
              <ShellButton type='button' $kind='plain' onClick={handleConfirm}>
                {t('additionalSales:pulse.retry.confirm_button')}
              </ShellButton>
              <ShellButton type='button' $kind='ghost' onClick={handleCancel}>
                {t('common:cancel')}
              </ShellButton>
            </ActionRow>
          </>
        )}
        {!isRetrying && !isInlineConfirm && (
          <>
            <RetryLine>
              <DirectionIcon>
                <IconHelp size={20} />
              </DirectionIcon>
              {t('additionalSales:pulse.retry.help')}
            </RetryLine>
            <ActionRow $center>
              <ShellButton
                type='button'
                $kind='ghost'
                disabled={!isEnabled}
                title={saleDataState?.saleDatabaseId ? undefined : t('additionalSales:pulse.retry.after_creation')}
                onClick={handleRetryClick}
              >
                {buttonText}
                <IconRestart size={20} />
              </ShellButton>
            </ActionRow>
            {helperText && <RetryHelp>{helperText}</RetryHelp>}
          </>
        )}
        {confirmationRequired && (
          <ShellConfirmModal
            isOpen={isModalOpen}
            title={retryText}
            cancelLabel={t('common:no_text')}
            confirmLabel={t('common:yes_text')}
            onCancel={handleCancel}
            onConfirm={handleConfirm}
            confirmDisabled={!isConfirmEnabled}
            closeable={!isRetrying}
          >
            <RetryModalBody
              isRetrying={isRetrying}
              confirmationRequired={confirmationRequired}
              emailChecked={emailChecked}
              spamChecked={spamChecked}
              onEmailChange={handleEmailChange}
              onSpamChange={handleSpamChange}
            />
          </ShellConfirmModal>
        )}
      </RetryBox>
    )
  }

  return (
    <>
      <SDButton block onClick={handleRetryClick} disabled={!isEnabled} size={SIZE.compact}>
        {buttonText}
      </SDButton>

      <RetryHelperText>{helperText}</RetryHelperText>

      <Modal onClose={handleCancel} isOpen={isModalOpen} animate autoFocus closeable={!isRetrying}>
        <ModalHeader>{retryText}</ModalHeader>
        <ModalBody>
          <RetryModalBody
            isRetrying={isRetrying}
            confirmationRequired={confirmationRequired}
            emailChecked={emailChecked}
            spamChecked={spamChecked}
            onEmailChange={handleEmailChange}
            onSpamChange={handleSpamChange}
          />
        </ModalBody>
        <ModalFooter>
          <ModalButton kind='tertiary' onClick={handleCancel} disabled={isRetrying}>
            {t('common:no_text')}
          </ModalButton>
          <ModalButton onClick={handleConfirm} disabled={!isConfirmEnabled}>
            {t('common:yes_text')}
          </ModalButton>
        </ModalFooter>
      </Modal>
    </>
  )
}

RetryLeadButton.propTypes = {
  actionsInProgress: PropTypes.bool,
  updateLeadDispatch: PropTypes.func.isRequired,
  saleDataState: PropTypes.shape({
    isRetryModalOpen: PropTypes.bool,
    isRetrying: PropTypes.bool,
    canRetry: PropTypes.bool,
    saleDatabaseId: PropTypes.number,
    createdBy: PropTypes.number,
    retryEmailChecked: PropTypes.bool,
    retrySpamChecked: PropTypes.bool,
  }).isRequired,
  callback: PropTypes.func.isRequired,
  hasBeenRetried: PropTypes.bool,
  creationDate: PropTypes.string,
  saleType: PropTypes.string,
  hidden: PropTypes.bool,
}

export default RetryLeadButton
